export const DEFAULT_CHANNEL_OPTIONS = [
  { channel_type: "email", channel_id: "inbox-1", label: "support@company.com" },
  { channel_type: "linkedin", channel_id: "linkedin-1", label: "LinkedIn" },
  { channel_type: "whatsapp", channel_id: "whatsapp-1", label: "WhatsApp" },
  { channel_type: "web", channel_id: "web-1", label: "Web Chat" },
];

export function formatTeamCreatedDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export function mapApiStatus(status, isArchived) {
  if (isArchived) return "archived";
  const s = (status || "").toLowerCase();
  if (s === "archived") return "archived";
  return "active";
}

export function mapApiTeamToTableRow(team) {
  const status = mapApiStatus(team.status, team.is_archived);
  const groups = team.groups || [];
  const firstGroupTitle = groups[0]?.title || team.groups_display || "—";

  return {
    id: team.id,
    name: team.name,
    description: team.description || "",
    group: firstGroupTitle,
    groups,
    groupIds: groups.map((g) => g.id || g._id).filter(Boolean),
    agents: team.agents_count ?? 0,
    totalChats: team.total_chats ?? 0,
    totalTickets: team.total_tickets ?? 0,
    status,
    created: formatTeamCreatedDate(team.created_at),
    channels: team.channels || [],
    raw: team,
  };
}

export function mapTeamMemberRoleLabel(roleRaw) {
  const r = (roleRaw || "agent").toLowerCase();
  if (r === "admin") return "Admin";
  if (r === "admin_with_billing") return "Super Admin";
  return "Agent";
}

export function mapApiMemberToUi(member) {
  const roleRaw = (member.role_name || member.role || "agent").toLowerCase();
  return {
    id: member._id || member.id,
    name: member.full_name || member.name || "",
    email: member.email || "",
    roleName: roleRaw,
    role: mapTeamMemberRoleLabel(roleRaw),
    avatar: member.agent_image || null,
  };
}

export function mapApiTeamToDetailView(team) {
  if (!team) return null;

  const members = (team.members || []).map(mapApiMemberToUi);
  const groups = (team.groups || []).map((g) => g.title || g.name).filter(Boolean);
  const channels = (team.channels || []).map((c) => c.label || c.channel_type).filter(Boolean);
  const lead = members[0];

  return {
    id: team.id,
    name: team.name,
    description: team.description || "",
    groups,
    groupIds: (team.groups || []).map((g) => g.id || g._id).filter(Boolean),
    channels,
    channelObjects: team.channels || [],
    members,
    teamLead: lead?.email || "—",
    createdDisplay: formatTeamCreatedDate(team.created_at),
    status: mapApiStatus(team.status, team.is_archived),
    stats: {
      agents: team.agents_count ?? members.length,
      totalChats: team.total_chats ?? 0,
      totalTickets: team.total_tickets ?? 0,
    },
    sidebarStats: {
      agents: team.agents_count ?? members.length,
      totalChats: team.total_chats ?? 0,
      totalTickets: team.total_tickets ?? 0,
    },
    raw: team,
  };
}

export function mapApiTeamToEditForm(team) {
  if (!team) return null;

  const channelKeys = (team.channels || []).map(
    (c) => `${c.channel_type}:${c.channel_id}`
  );

  return {
    name: team.name || "",
    description: team.description || "",
    channels: channelKeys,
    channelObjects: team.channels || [],
    groupIds: (team.groups || []).map((g) => g.id || g._id).filter(Boolean),
    groupSelect: "",
    members: (team.members || []).map(mapApiMemberToUi),
  };
}

export function getChannelSelectOptions() {
  return DEFAULT_CHANNEL_OPTIONS.map((c) => ({
    value: `${c.channel_type}:${c.channel_id}`,
    label: c.label,
  }));
}

export function buildTeamPayloadFromForm(form) {
  const channelMap = Object.fromEntries(
    DEFAULT_CHANNEL_OPTIONS.map((c) => [`${c.channel_type}:${c.channel_id}`, c])
  );

  const channels = (form.channels || [])
    .map((key) => channelMap[key] || form.channelObjects?.find(
      (c) => `${c.channel_type}:${c.channel_id}` === key
    ))
    .filter(Boolean)
    .map(({ channel_type, channel_id, label }) => ({
      channel_type,
      channel_id,
      label,
    }));

  return {
    name: form.name?.trim(),
    description: form.description?.trim() || "",
    group_ids: form.groupIds || [],
    member_ids: (form.members || []).map((m) => m.id).filter(Boolean),
    channels,
  };
}

export function computeTeamTabCounts(teams = []) {
  return teams.reduce(
    (acc, row) => {
      acc.all += 1;
      if (row.status === "archived") acc.archived += 1;
      else acc.active += 1;
      return acc;
    },
    { all: 0, active: 0, archived: 0 }
  );
}

export function mapGroupListItem(item) {
  return {
    id: item?.id || item?._id,
    label: item?.title || item?.name || "",
  };
}

export function mapAgentListItem(item) {
  const u = item?.user || item;
  const roleRaw = (u?.role_name || "agent").toLowerCase();
  return {
    id: u?._id || u?.id,
    name: u?.full_name || u?.name || "",
    email: u?.email || "",
    roleName: roleRaw,
    role: mapTeamMemberRoleLabel(roleRaw),
    avatar: u?.agent_image || null,
  };
}

/** Payload for POST /team/create from the create-team modal. */
export function buildTeamCreatePayloadFromForm(form) {
  const members = (form.memberIds || []).map((id) => ({ id }));
  return buildTeamPayloadFromForm({
    name: form.name,
    description: form.description,
    groupIds: form.groupId ? [form.groupId] : [],
    members,
    channels: form.channels,
  });
}
